import { FormError, SField, SInput, STooltip, SIcon } from "@superwall/legacy-ui"
import { forwardRef, InputHTMLAttributes, PropsWithoutRef, useRef } from "react"
import { useEnhancedField } from "./DisabledContext"

export interface LabeledFileFieldProps extends PropsWithoutRef<JSX.IntrinsicElements["input"]> {
  name: string
  label: string
  nullOnEmpty?: boolean
  helpText?: string
  warningText?: string
  isBoldWhiteLabel?: boolean
  /** Whether a key is already persisted in the database */
  hasPersistedKey?: boolean
}
export const LabeledFileField = forwardRef<HTMLInputElement, LabeledFileFieldProps>(
  ({ helpText, warningText, isBoldWhiteLabel, hasPersistedKey, ...props }, ref) => {
    const { name, label, nullOnEmpty = false } = props
    const fileInputRef = useRef<HTMLInputElement>(null)
    const {
      input: { onChange, value, ...inputRest },
      meta: { touched, error, submitError, submitting },
      extra: { isDisabled },
      ...rest
    } = useEnhancedField(name, {
      parse: (value) => {
        if (props.type === "number") {
          return Number(value)
        }
        if (nullOnEmpty) {
          return value === "" ? null : value
        }
        return value
      },
      allowNull: nullOnEmpty,
    })

    const hasLocalSelectedFile = !!value?.filename
    const hasKey = hasPersistedKey || hasLocalSelectedFile

    const filename = value?.filename || ""
    const normalizedError = Array.isArray(error) ? error.join(", ") : error || submitError

    const handleButtonClick = () => {
      fileInputRef.current?.click()
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        file.text().then((content) => {
          onChange({ filename: file.name, content })
        })
      }
    }

    const handleRemoveFile = () => {
      onChange(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }

    return (
      <SField>
        <SField.Label>
          <div className="flex flex-row  items-center">
            {helpText ? (
              <div className="relative">
                <STooltip content={helpText} width="240px" placement="top-start">
                  <span className={isBoldWhiteLabel ? "text-white font-bold" : ""}>{label}</span>
                </STooltip>
              </div>
            ) : (
              <span className={isBoldWhiteLabel ? "text-white font-bold" : ""}>{label}</span>
            )}
            {warningText ? (
              <div className="relative">
                <STooltip content={warningText} type="warning" width="240px" placement="top-start">
                  <span>{label}</span>
                </STooltip>
              </div>
            ) : null}
          </div>
        </SField.Label>

        <div className="flex flex-col gap-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
            disabled={isDisabled}
            {...props}
          />

          {hasKey && !hasLocalSelectedFile ? (
            <div className="flex flex-row gap-2">
              <div className="flex-1 appearance-none rounded-md border border-white/10 bg-grayscale-lightest/10 py-2 px-3 text-green-400 flex items-center gap-2">
                <SIcon type="check" className="size-3" />
                <span>Uploaded</span>
              </div>
              <button
                type="button"
                onClick={handleButtonClick}
                disabled={isDisabled}
                className="appearance-none shadow-none rounded-md border border-white/10 hover:border-white/25 bg-grayscale-lightest/10 py-2 px-3 text-white/60 hover:text-white whitespace-nowrap disabled:text-grayscale-lightest disabled:cursor-not-allowed"
              >
                Upload new file
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleButtonClick}
              disabled={isDisabled}
              className={`appearance-none shadow-none rounded-md border border-white border-opacity-10 focus:outline-0 focus:border-solid focus:border-opacity-25 hover:border-opacity-25 bg-grayscale-lightest bg-opacity-10 py-2 px-3 w-full text-white text-left disabled:text-grayscale-lightest disabled:cursor-not-allowed ${
                filename ? "flex items-center justify-between" : ""
              }`}
            >
              {filename ? (
                <>
                  <span>{filename}</span>
                  <span
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveFile()
                    }}
                    className="text-red-400 hover:text-red-300 cursor-pointer"
                  >
                    <SIcon type="trash" className="size-4" />
                  </span>
                </>
              ) : (
                <span className="text-white text-opacity-60">
                  {hasPersistedKey !== undefined ? "Upload file" : "Choose file..."}
                </span>
              )}
            </button>
          )}
        </div>

        {touched && normalizedError && (
          <FormError hasLabel={label === "" ? false : true} normalizedError={normalizedError} />
        )}
      </SField>
    )
  }
)

LabeledFileField.displayName = "LabeledFileField"
